import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, FlaskConical, History, Layers, TrendingUp, XCircle, Boxes } from "lucide-react";
import { toast } from "sonner";
import { MetricCard, MetricGrid } from "@/components/MetricCard";
import { StatusBadge } from "@/components/StatusBadge";
import { EnvironmentBadge } from "@/components/EnvironmentBadge";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime, formatDuration } from "@/lib/datetime";

const fmtDuration = formatDuration;

function TrendBars({ data }) {
  const max = Math.max(1, ...data.map((d) => d.passed + d.failed));
  return (
    <div className="flex items-end gap-1.5 h-32 px-1" data-testid="trend-chart">
      {data.map((d) => {
        const total = d.passed + d.failed;
        const heightPct = total === 0 ? 6 : Math.max(6, (total / max) * 100);
        const passPct = total === 0 ? 0 : (d.passed / total) * 100;
        return (
          <div key={d.date} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${d.date}: ${d.passed} passed, ${d.failed} failed`}>
            <div className="w-full rounded-t-sm overflow-hidden bg-[#F1F5F9]" style={{ height: `${heightPct}%` }}>
              <div className="w-full bg-[#10B981]" style={{ height: `${passPct}%` }} />
              <div className="w-full bg-[#EF4444]" style={{ height: `${100 - passPct}%` }} />
            </div>
            <span className="text-[9px] text-[#94A3B8]">{d.date.slice(5)}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function ProjectOverviewPage() {
  const { projectId } = useParams();
  const [data, setData] = useState(null);
  const [envSummary, setEnvSummary] = useState(null);

  useEffect(() => {
    api.get(`/projects/${projectId}/dashboard`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
    api.get(`/projects/${projectId}/environments-summary`)
      .then((r) => setEnvSummary(r.data.items || []))
      .catch(() => setEnvSummary([]));
  }, [projectId]);

  if (!data) {
    return <div className="surface-card p-6 text-sm text-[#64748B]">Loading…</div>;
  }

  return (
    <div data-testid="project-overview-page">
      <MetricGrid columns={4}>
        <MetricCard
          testId="overview-total-tests"
          label="Total Tests"
          icon={FlaskConical}
          value={data.total_tests}
        />
        <MetricCard
          testId="overview-total-suites"
          label="Suites"
          icon={Layers}
          value={data.total_suites}
        />
        <MetricCard
          testId="overview-total-runs"
          label="Total Runs"
          icon={History}
          value={data.total_runs}
        />
        <MetricCard
          testId="overview-pass-rate"
          label="Pass Rate (30d)"
          icon={TrendingUp}
          value={data.pass_rate == null ? "—" : `${data.pass_rate}%`}
          hint={data.pass_rate == null ? "No runs yet" : `${data.passed_count_30d} passed · ${data.failed_count_30d} failed`}
        />
      </MetricGrid>

      {envSummary && envSummary.length > 0 && (
        <div className="surface-card p-5 mb-5" data-testid="env-summary-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-['Geist'] font-semibold text-sm tracking-tight text-[#0F172A] inline-flex items-center gap-2"><Boxes size={14} /> Environments</h3>
            <Link to={`/projects/${projectId}/environments`} className="text-xs text-[#3B82F6] hover:underline">Manage →</Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {envSummary.map((e) => (
              <Link
                key={e.id}
                to={`/projects/${projectId}/environments/${e.id}`}
                data-testid={`env-summary-${e.id}`}
                className="block rounded-lg border border-[#E5E7EB] dark:border-[#1F2937] p-3 hover:border-[#3B82F6] dark:hover:border-[#3B82F6] transition-colors"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-sm text-[#0F172A]">{e.name}</span>
                  <EnvironmentBadge type={e.type} />
                </div>
                <p className="text-[10px] font-mono text-[#94A3B8] truncate mb-2">{e.base_url?.replace(/^https?:\/\//, "") || "—"}</p>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-[#64748B]">Pass rate</span>
                  <span className="font-medium text-[#0F172A]">{e.pass_rate == null ? "—" : `${e.pass_rate}%`}</span>
                </div>
                <div className="flex items-center justify-between text-xs mt-1">
                  <span className="text-[#64748B]">{e.passed} passed · {e.failed} failed</span>
                  {e.last_run_status && <StatusBadge status={e.last_run_status} />}
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}


      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 surface-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-['Geist'] font-semibold text-sm tracking-tight text-[#0F172A]">Runs over the last 14 days</h3>
            <div className="flex items-center gap-3 text-[10px] text-[#64748B]">
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#10B981]" /> Passed</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-[#EF4444]" /> Failed</span>
            </div>
          </div>
          <TrendBars data={data.trend} />
        </div>

        <div className="surface-card p-5">
          <h3 className="font-['Geist'] font-semibold text-sm tracking-tight text-[#0F172A] mb-4">Recent activity</h3>
          {data.recent_runs.length === 0 ? (
            <p className="text-sm text-[#64748B]">No runs yet for this project.</p>
          ) : (
            <ul className="space-y-3" data-testid="recent-activity-list">
              {data.recent_runs.map((r) => (
                <li key={r.id} className="flex items-start gap-3 text-sm">
                  {r.status === "passed" ? (
                    <CheckCircle2 size={14} className="text-[#10B981] mt-0.5 shrink-0" />
                  ) : r.status === "failed" ? (
                    <XCircle size={14} className="text-[#EF4444] mt-0.5 shrink-0" />
                  ) : (
                    <span className="w-2 h-2 rounded-full bg-[#3B82F6] mt-2 shrink-0" />
                  )}
                  <div className="min-w-0 flex-1">
                    <Link
                      to={r.kind === "suite" ? `/suite-runs/${r.id}` : `/runs/${r.id}`}
                      className="font-medium text-[#0F172A] truncate hover:text-[#3B82F6] block"
                      data-testid={`recent-run-${r.id}`}
                    >
                      {r.name || "Untitled run"}
                    </Link>
                    <p className="text-xs text-[#94A3B8]">
                      {r.kind === "suite" ? "Suite run" : "Test"} · {fmtDuration(r.duration_ms)} · {formatDateTime(r.started_at)}
                    </p>
                  </div>
                  <StatusBadge status={r.status} />
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
